import React, {useState, useMemo, useEffect} from 'react';
import { Plus, Eye, Ban, CheckSquare, Search } from 'lucide-react';
import useApi from "../../../utils/useApi";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import Swal from 'sweetalert2';
import {Link, useNavigate} from "react-router-dom";
import { useNavCount } from "../../../contexts/NavCountProvider";
import PortalActions from "../ClientActions/PortalActions";

const PortalRequestsDashboard = ({  successMessage, handleShowHeader, handleShowSettingsHeader }) => {
    const { refreshCounts } = useNavCount();
    const {getPortalRequests, approvePortalRequest, denyPortalRequest} = useApi();
    const [clients, setClients] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'client', direction: 'asc' });
    const navigate = useNavigate();

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initializeData();
    }, []);

    const initializeData = async () => {
        setIsLoading(true);
        const response = await getPortalRequests();

        setClients(response.clients);

        setIsLoading(false);
        await refreshCounts();
    }



    const handleSort = (key) => {
        setSortConfig({
                          key,
                          direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                      });
    };

    const sortedClients = useMemo(() => {
        if (!clients) return [];

        return [...clients].sort((a, b) => {
            let aValue = sortConfig.key === 'client' ? a.profile.client :
                         sortConfig.key === 'client_id' ? a.client_id :
                         a[sortConfig.key];
            let bValue = sortConfig.key === 'client' ? b.profile.client :
                         sortConfig.key === 'client_id' ? b.client_id :
                         b[sortConfig.key];

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [clients, sortConfig]);

    const filteredClients = useMemo(() => {
        return sortedClients.filter(client =>
                                        client.profile.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        client.client_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        client.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [sortedClients, searchTerm]);

    const getClientStatus = (newClient) => {
        if (newClient == 1) return "New Client";
        if (newClient == 0) return "Returning Client";
        return "Not Specified";
    };

    if(isLoading) {
        return (<LoadingScreen/>);
    }

    return (
        <div className="min-h-screen p-4 md:p-6 bg-white dark:bg-gray-900 page-container relative z-10">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                        Portal Requests
                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
              ({clients?.length || 0})
            </span>
                    </h1>
                    <Link
                        to={`/new-client`}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors w-full md:w-auto justify-center"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Client
                    </Link>
                </div>

                {/* Success Message */}
                {successMessage && (
                    <div className="mb-6 p-4 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-100 rounded-lg">
                        {successMessage}
                    </div>
                )}

                {/* Search Input */}
                <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search clients..."
                        className="w-full pl-10 pr-4 py-2 rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Mobile Cards / Desktop Table */}
                <div className="block md:hidden">
                    {filteredClients.map((client, index) => (
                        <div key={index} className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-600 dark:text-gray-300">Client Name</span>
                                    <span className="text-gray-900 dark:text-white">{client.profile.client}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-600 dark:text-gray-300">Client ID</span>
                                    <span className="text-gray-900 dark:text-white">{client.client_id}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-600 dark:text-gray-300">Email</span>
                                    <span className="text-gray-900 dark:text-white">{client.email}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-600 dark:text-gray-300">Status</span>
                                    <span className="text-gray-900 dark:text-white">
                                    {getClientStatus(client.profile.new_client)}
                                  </span>
                                </div>
                                <div className="flex flex-row gap-2 pt-3">


                                    <PortalActions onRefresh={initializeData} client={client} />

                                    <Link
                                        to={`/client/${client.id}/edit`}
                                        className="w-full inline-flex items-center justify-center px-3 py-2 border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white rounded transition-colors"
                                    >
                                        <Eye className="w-4 h-4 mr-2"/>
                                        View Details
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <div className="bg-gray-50 dark:bg-gray-800">
                            <div className="grid grid-cols-5 gap-4">
                                {['Client Name', 'Client ID', 'Email', 'New/Returning Client', 'Actions'].map((header, index) => (
                                    <div
                                        key={index}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                                        onClick={() => handleSort(header.toLowerCase().replace(/ /g, '_'))}
                                    >
                                        {header}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredClients.map((client, index) => (
                                <div key={index} className="grid grid-cols-5 gap-4 hover:bg-gray-50 dark:hover:bg-gray-800">
                                    <div className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                                        {client.profile.client}
                                    </div>
                                    <div className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                                        {client.client_id}
                                    </div>
                                    <div className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                                        {client.email}
                                    </div>
                                    <div className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                                        {getClientStatus(client.profile.new_client)}
                                    </div>
                                    <div className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col md:flex-row gap-2 md:gap-1 justify-start items-center flex-wrap">
                                            <PortalActions onRefresh={initializeData} client={client} />

                                            <Link
                                                to={`/client/${client.id}/edit`}
                                                className="w-auto inline-flex items-center px-3 py-2 border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white rounded transition-colors text-xs mx-0"
                                            >
                                                <Eye className="w-4 h-4 mr-1"/>
                                                View
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortalRequestsDashboard;
